// src/features/home/components/TaskRow.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, differenceInCalendarDays } from 'date-fns';
import { Box, Chip, TableCell, TableRow, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { parseUTCToLocalTimezone, getDateLocale } from '../../../i18n/LocalizedDateDistance';
import { getStateConfig } from '../../tasks/utils/taskStateUtils';

export function isDueYet(task) {
  if (!task.due_date) return false;
  const dueDate = new Date(task.due_date);
  const today = new Date();
  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return dueDate <= today;
}

function DueDateCell({ dueDate, language }) {
  if (!dueDate) return <Typography variant="body2" color="text.disabled">-</Typography>;

  const dateObj = parseUTCToLocalTimezone(dueDate, language);

  // Urgency is already signalled by the first column; keep the date itself quiet.
  return (
    <Typography variant="body2" color="text.secondary" noWrap>
      {format(dateObj, 'd. MMM', { locale: getDateLocale(language) })}
    </Typography>
  );
}

function UrgencyCell({ dueDate, language, t }) {
  if (!dueDate) return null;

  const dateObj = parseUTCToLocalTimezone(dueDate, language);
  const daysFromNow = differenceInCalendarDays(dateObj, new Date());

  // Only overdue/today get a chip, so the rows that need action stand out;
  // everything further out is plain text.
  if (daysFromNow < 0) {
    return (
      <Chip
        label={t('tasks:details.overdue')}
        size="small"
        color="error"
        variant="filled"
        sx={{ fontWeight: 600, minWidth: 80 }}
      />
    );
  }

  if (daysFromNow === 0) {
    return (
      <Chip
        label={t('tasks:details.due_today')}
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontWeight: 600, minWidth: 80 }}
      />
    );
  }

  return (
    <Typography
      variant="body2"
      noWrap
      color={daysFromNow === 1 ? 'warning.main' : 'text.secondary'}
      fontWeight={daysFromNow === 1 ? 600 : 400}
    >
      {daysFromNow === 1
        ? t('tasks:details.due_tomorrow')
        : t('tasks:details.days_left', { count: daysFromNow })}
    </Typography>
  );
}

// "Waiting to be done" is the default for every row here, so a chip saying so
// is noise — only show the status once something has actually happened.
const QUIET_STATES = ['PENDING', 'SCHEDULED'];

/**
 * One task's row: urgency chip, title (+ optional badge), due date, status chip, chevron.
 * Shared between "Mine opgaver" and the expanded category task lists on the homepage.
 */
export default function TaskRow({ task, badge, showStatusOnlyIfDue = false }) {
  const { t, i18n } = useTranslation(['tasks']);
  const navigate = useNavigate();

  const stateConfig = getStateConfig(task.state);
  const StateIcon = stateConfig.icon;
  const showStatus = !QUIET_STATES.includes(task.state) && (!showStatusOnlyIfDue || isDueYet(task));

  return (
    <TableRow
      hover
      onClick={() => navigate(`/tasks/${task.task_id}`)}
      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
    >
      <TableCell sx={{ pl: 0 }}>
        <UrgencyCell dueDate={task.due_date} language={i18n.language} t={t} />
      </TableCell>
      {/* width 100% + maxWidth 0: the title takes the leftover space and ellipsizes instead of widening the table */}
      <TableCell sx={{ width: '100%', maxWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
            {task.title}
          </Typography>
          {badge}
        </Box>
      </TableCell>
      <TableCell>
        <DueDateCell dueDate={task.due_date} language={i18n.language} />
      </TableCell>
      <TableCell>
        {showStatus && (
          <Chip
            icon={<StateIcon />}
            label={stateConfig.label}
            size="small"
            color={stateConfig.color}
            variant="outlined"
          />
        )}
      </TableCell>
      <TableCell sx={{ pr: 3, width: 32 }}>
        <ChevronRightIcon sx={{ color: 'text.tertiary' }} fontSize="small" />
      </TableCell>
    </TableRow>
  );
}
